/**
 * Google Analytics 4 integration for SociBaba.
 *
 * The GA Measurement ID is stored in `app_config` and controlled by a
 * super-admin in Admin Setup. On first mount the app fetches the public
 * endpoint, injects the gtag script + config, then routes go through
 * `usePageviewTracker()` so every SPA navigation fires a page_view.
 *
 * Ecommerce helpers wrap the standard GA4 events (view_item, select_item,
 * view_item_list, add_to_cart, remove_from_cart, begin_checkout, purchase)
 * so any surface in the app can `import { track } from '@/lib/analytics'`
 * without knowing the underlying vendor.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

declare global {
  interface Window {
    dataLayer?: any[];
    gtag?: (...args: any[]) => void;
    fbq?: any;
    _fbq?: any;
    __sb_ga_id?: string;
    __sb_ga_ready?: boolean;
    __sb_ga_debug?: boolean;
    __sb_px_id?: string;
    __sb_px_ready?: boolean;
    __sb_analytics_cfg?: {
      ga_measurement_id: string;
      ga_debug_mode: boolean;
      meta_pixel_id: string;
    };
  }
}

const API = process.env.REACT_APP_BACKEND_URL as string;
const GA_STATE_KEY = "sb_ga_config_v1";
const CONSENT_KEY = "sb_consent_v1";  // "granted" | "denied" | null

export type ConsentDecision = "granted" | "denied" | null;

export function getConsent(): ConsentDecision {
  if (typeof window === "undefined") return null;
  const v = localStorage.getItem(CONSENT_KEY);
  return v === "granted" || v === "denied" ? v : null;
}

export function setConsent(v: "granted" | "denied") {
  localStorage.setItem(CONSENT_KEY, v);
  window.dispatchEvent(new CustomEvent("sb-consent-changed", { detail: v }));
  if (v === "granted") {
    // Fire the deferred bootstrap
    initAnalytics();
  }
}

/** Inject the gtag script tag once. Idempotent. */
function loadGtag(measurementId: string, debug: boolean) {
  if (window.__sb_ga_ready) return;
  if (!measurementId) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag("js", new Date());
  const cfg: Record<string, any> = {
    send_page_view: false,
    anonymize_ip: true,
  };
  if (debug) cfg.debug_mode = true;
  window.gtag("config", measurementId, cfg);

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script);

  window.__sb_ga_id = measurementId;
  window.__sb_ga_debug = debug;
  window.__sb_ga_ready = true;
}

/** Inject the Meta Pixel. Idempotent. */
function loadMetaPixel(pixelId: string) {
  if (window.__sb_px_ready) return;
  if (!pixelId) return;

  (function (f: any, b, e, v, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = !0; n.version = "2.0"; n.queue = [];
    t = b.createElement(e); t.async = !0;
    t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
  window.__sb_px_id = pixelId;
  window.__sb_px_ready = true;
}

/** Capture the current page's UTM/referrer as a first-touch visit. */
async function captureUtmVisit() {
  try {
    const q = new URLSearchParams(window.location.search);
    await axios.post(`${API}/api/analytics/utm-visit`, {
      path: window.location.pathname,
      referrer: document.referrer || "",
      landing_url: window.location.href,
      utm_source: q.get("utm_source") || "",
      utm_medium: q.get("utm_medium") || "",
      utm_campaign: q.get("utm_campaign") || "",
      utm_term: q.get("utm_term") || "",
      utm_content: q.get("utm_content") || "",
    }, { timeout: 3000 });
  } catch {
    /* best-effort */
  }
}

/**
 * Fetch analytics config and wire up gtag + Meta Pixel. Runs on app boot.
 *
 * Respects consent: if the user hasn't decided yet OR has denied consent, we
 * skip loading trackers. UTM visits are captured server-side regardless
 * (anonymised — hashed IP + no full IP or query PII).
 */
export async function initAnalytics(): Promise<void> {
  // UTM capture is consent-independent — the server anonymises IP and only
  // keeps aggregate campaign dimensions. Fire and forget.
  captureUtmVisit();

  const consent = getConsent();
  if (consent !== "granted") return; // Wait for user opt-in.

  try {
    // Fast path — use cached config from sessionStorage
    const cached = sessionStorage.getItem(GA_STATE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed?.ga_measurement_id) loadGtag(parsed.ga_measurement_id, !!parsed.ga_debug_mode);
      if (parsed?.meta_pixel_id) loadMetaPixel(parsed.meta_pixel_id);
      window.__sb_analytics_cfg = parsed;
    }
    const res = await axios.get(`${API}/api/analytics/public`, { timeout: 4000 });
    const cfg = res.data || {};
    if (cfg.ga_measurement_id) loadGtag(cfg.ga_measurement_id, !!cfg.ga_debug_mode);
    if (cfg.meta_pixel_id) loadMetaPixel(cfg.meta_pixel_id);
    sessionStorage.setItem(GA_STATE_KEY, JSON.stringify(cfg));
    window.__sb_analytics_cfg = cfg;
  } catch {
    // Analytics is best-effort — never break the app if unreachable.
  }
}

/** Fire a single GA4 event. Safe to call before gtag has loaded. */
export function track(event: string, params: Record<string, any> = {}) {
  if (typeof window === "undefined") return;
  if (window.gtag) window.gtag("event", event, params);
  // Mirror the most important events to Meta Pixel.
  if (window.fbq) {
    const map: Record<string, string> = {
      view_item: "ViewContent",
      add_to_cart: "AddToCart",
      begin_checkout: "InitiateCheckout",
      add_payment_info: "AddPaymentInfo",
      purchase: "Purchase",
      sign_up: "CompleteRegistration",
      search: "Search",
      login: "Lead",
    };
    const fbEvent = map[event];
    if (fbEvent) {
      const p: Record<string, any> = {};
      if (params.value !== undefined) p.value = params.value;
      if (params.currency) p.currency = params.currency;
      if (params.transaction_id) p.order_id = params.transaction_id;
      if (params.search_term) p.search_string = params.search_term;
      window.fbq("track", fbEvent, p);
    }
  }
}

// -------- E-commerce helpers (GA4 standard events) --------
export interface AnalyticsItem {
  item_id: string;
  item_name: string;
  item_brand?: string;
  item_category?: string;
  item_category2?: string;
  price?: number;
  currency?: string;
  quantity?: number;
  index?: number;
}

export const ecom = {
  viewItem: (item: AnalyticsItem, extra: Record<string, any> = {}) =>
    track("view_item", {
      currency: item.currency || "INR",
      value: item.price,
      items: [item],
      ...extra,
    }),

  viewItemList: (list_name: string, items: AnalyticsItem[]) =>
    track("view_item_list", { item_list_name: list_name, items }),

  selectItem: (list_name: string, item: AnalyticsItem) =>
    track("select_item", { item_list_name: list_name, items: [item] }),

  addToCart: (item: AnalyticsItem) =>
    track("add_to_cart", {
      currency: item.currency || "INR",
      value: (item.price || 0) * (item.quantity || 1),
      items: [item],
    }),

  removeFromCart: (item: AnalyticsItem) =>
    track("remove_from_cart", {
      currency: item.currency || "INR",
      value: (item.price || 0) * (item.quantity || 1),
      items: [item],
    }),

  beginCheckout: (items: AnalyticsItem[], value: number, currency = "INR") =>
    track("begin_checkout", { currency, value, items }),

  addShippingInfo: (items: AnalyticsItem[], value: number, tier: string, currency = "INR") =>
    track("add_shipping_info", { currency, value, shipping_tier: tier, items }),

  addPaymentInfo: (items: AnalyticsItem[], value: number, type: string, currency = "INR") =>
    track("add_payment_info", { currency, value, payment_type: type, items }),

  purchase: (transaction_id: string, items: AnalyticsItem[], value: number,
              opts: { currency?: string; tax?: number; shipping?: number; coupon?: string } = {}) =>
    track("purchase", {
      transaction_id,
      currency: opts.currency || "INR",
      value,
      tax: opts.tax,
      shipping: opts.shipping,
      coupon: opts.coupon,
      items,
    }),

  refund: (transaction_id: string, value: number, items?: AnalyticsItem[]) =>
    track("refund", { transaction_id, value, currency: "INR", items }),

  search: (search_term: string) => track("search", { search_term }),

  signUp: (method: string) => track("sign_up", { method }),

  login: (method: string) => track("login", { method }),

  share: (content_type: string, item_id?: string) =>
    track("share", { content_type, item_id }),
};

/**
 * React hook — fires `page_view` on every SPA route change.
 * Safe to include before GA has loaded (calls are queued via dataLayer).
 */
export function usePageviewTracker() {
  const location = useLocation();
  const lastPath = useRef<string>("");
  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === lastPath.current) return;
    lastPath.current = path;
    if (window.gtag && window.__sb_ga_id) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: document.title,
        page_location: window.location.href,
      });
    }
  }, [location.pathname, location.search]);
}
